import { memo, useMemo, type FC } from "react";
import { CopyPlus, Trash2 } from "lucide-react";
import {
  CONDITION_CATEGORIES,
  type ConditionCategory,
  type SegmentCondition,
} from "../CreateSegment.Constants";
import { isConditionComplete } from "../CreateSegment.Utils";
import { useSegmentFormatters, useSegmentLabel } from "../UseSegmentLabel";
import { SegmentSelect } from "./ConditionControls";
import ListConditionFields from "./Fields/ListConditionFields";
import LocationConditionFields from "./Fields/LocationConditionFields";
import OrderActivityFields from "./Fields/OrderActivityFields";
import SignupDateFields from "./Fields/SignupDateFields";

interface SegmentConditionRowProps {
  condition: SegmentCondition;
  position: number;
  peopleCount: number | null;
  showError: boolean;
  canDuplicate: boolean;
  canRemove: boolean;
  onChange: (_condition: SegmentCondition) => void;
  onChangeCategory: (_id: number, _category: ConditionCategory) => void;
  onDuplicate: (_id: number) => void;
  onRemove: (_id: number) => void;
}

const ConditionFields: FC<Pick<SegmentConditionRowProps, "condition" | "onChange">> = ({
  condition,
  onChange,
}) => {
  const label = useSegmentLabel();
  switch (condition.category) {
    case "list":
      return <ListConditionFields condition={condition} onChange={onChange} />;
    case "location":
      return <LocationConditionFields condition={condition} onChange={onChange} />;
    case "orderActivity":
      return <OrderActivityFields condition={condition} onChange={onChange} />;
    case "signupDate":
      return <SignupDateFields condition={condition} onChange={onChange} />;
    default:
      return <p className="segment-builder__helper">{label("conditions.helper")}</p>;
  }
};

const SegmentConditionRow: FC<SegmentConditionRowProps> = ({
  condition,
  position,
  peopleCount,
  showError,
  canDuplicate,
  canRemove,
  onChange,
  onChangeCategory,
  onDuplicate,
  onRemove,
}) => {
  const label = useSegmentLabel();
  const { formatValue } = useSegmentFormatters();
  const isComplete = isConditionComplete(condition);

  const categoryOptions = useMemo(
    () =>
      CONDITION_CATEGORIES.map(category => ({
        value: category,
        label: label(`categories.${category}.option`),
        selectedLabel: label(`categories.${category}.selected`),
      })),
    [label]
  );

  return (
    <div
      className={`segment-builder__condition ${
        showError && !isComplete ? "segment-builder__condition--error" : ""
      }`}
    >
      <span className="segment-builder__condition-number">{formatValue(position)}</span>
      <div className="segment-builder__condition-body">
        <SegmentSelect
          className="segment-builder__category"
          popupClassName="segment-builder__category-popup"
          aria-label={label("conditions.categoryAria", { number: position })}
          value={condition.category}
          placeholder={label("conditions.categoryPlaceholder")}
          options={categoryOptions}
          optionLabelProp="selectedLabel"
          onChange={(category: ConditionCategory) => onChangeCategory(condition.id, category)}
        />
        <ConditionFields condition={condition} onChange={onChange} />
      </div>
      <div className="segment-builder__condition-aside">
        {peopleCount !== null && (
          <span className="segment-builder__condition-count">
            {label("conditions.people", { count: peopleCount, value: formatValue(peopleCount) })}
          </span>
        )}
        <button
          type="button"
          className="segment-builder__icon-btn"
          aria-label={label("conditions.duplicate")}
          disabled={!canDuplicate}
          onClick={() => onDuplicate(condition.id)}
        >
          <CopyPlus size={18} />
        </button>
        <button
          type="button"
          className="segment-builder__icon-btn segment-builder__icon-btn--danger"
          aria-label={label("conditions.remove")}
          disabled={!canRemove}
          onClick={() => onRemove(condition.id)}
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  );
};

export default memo(SegmentConditionRow);
